// Shared derivations for the Elvis conversations page. The insight charts and
// the drill-down modal count the same way, so a bar's value always matches the
// number of rows behind it.

// Words carrying no review value - stripped before keyword counting.
export const STOP_WORDS = new Set([
    'the', 'and', 'for', 'are', 'but', 'not', 'you', 'your', 'yours', 'with', 'that', 'this', 'these',
    'those', 'have', 'has', 'had', 'was', 'were', 'from', 'what', 'when', 'where', 'which', 'who',
    'whom', 'why', 'how', 'can', 'cant', 'could', 'would', 'should', 'will', 'shall', 'does', 'did',
    'doing', 'done', 'been', 'being', 'into', 'onto', 'over', 'under', 'than', 'then', 'them', 'they',
    'their', 'there', 'here', 'about', 'after', 'before', 'between', 'because', 'also', 'any', 'all',
    'our', 'ours', 'out', 'get', 'got', 'its', 'please', 'need', 'needs', 'know', 'tell', 'show',
    'give', 'want', 'make', 'made', 'just', 'like', 'some', 'more', 'most', 'much', 'many', 'very',
    'one', 'two', 'new', 'use', 'used', 'using', 'see', 'look', 'find', 'help', 'thanks', 'thank',
    'hello', 'elvis', 'anyone', 'somebody', 'something', 'anything', 'able', 'let', 'lets', 'now',
    'still', 'only', 'same', 'other', 'each', 'both', 'own', 'off', 'per', 'via', 'yes'
]);

// Star ratings arrive as null (never rated) or 1-5. Highest first, so every
// breakdown reads like a review summary rather than an arbitrary order.
export const RATING_STARS = [5, 4, 3, 2, 1];

export const UNRATED = 'unrated';

// Keys are strings, so one key type covers both the star levels and 'unrated'.
export const RATING_ORDER = [...RATING_STARS.map(String), UNRATED];

export const RATING_LABELS = {
    5: '5 stars',
    4: '4 stars',
    3: '3 stars',
    2: '2 stars',
    1: '1 star',
    [UNRATED]: 'Unrated'
};

// Tolerate numeric strings, and treat anything off the 1-5 scale - null
// included - as unrated rather than inventing a level for it.
export const starsFrom = value => {
    const stars = Number(value);
    return Number.isInteger(stars) && stars >= 1 && stars <= 5 ? stars : null;
};

export const starsOf = row => starsFrom(row?.rating);

// The bucket a row belongs to: '1' - '5', or 'unrated'.
export const ratingOf = row => {
    const stars = starsOf(row);
    return stars === null ? UNRATED : String(stars);
};

// The distinct review-worthy words in one question.
export const questionTokens = question => {
    const tokens = new Set();
    if (!question) return tokens;
    String(question)
        .toLowerCase()
        .replace(/[^a-z0-9\s'-]/g, ' ')
        .split(/\s+/)
        .forEach(raw => {
            const word = raw.replace(/^[-']+|[-']+$/g, '');
            if (word.length < 3 || STOP_WORDS.has(word) || /^\d+$/.test(word)) return;
            tokens.add(word);
        });
    return tokens;
};

// Counts each keyword once per question, so the answer is
// "how many questions mention this" rather than raw term frequency.
export const countKeywords = rows => {
    const counts = new Map();
    rows.forEach(row => questionTokens(row.question).forEach(word => {
        counts.set(word, (counts.get(word) ?? 0) + 1);
    }));
    return [...counts.entries()].sort(([, a], [, b]) => b - a);
};

// Ranked [name, count] pairs, largest first.
export const rankBy = (rows, pick) => {
    const counts = new Map();
    rows.forEach(row => {
        const key = pick(row);
        counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return [...counts.entries()].sort(([, a], [, b]) => b - a);
};

export const userOf = row => row.user || 'Unknown';

export const knowledgeOf = row => row.knowledgeName || 'No match';

export const pct = (part, whole) => whole > 0 ? ((part / whole) * 100).toFixed(2) : 0;
